import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence, TargetAndTransition } from 'framer-motion';
import { cn } from './lib/utils';

export interface JourneyStep {
  id: string;
  title: string;
  description: string;
  image: string;
}

const journeySteps: JourneyStep[] = [
  {
    id: "01",
    title: "Initial Consultation",
    description:
      "Meet with our expert migration agents to discuss your goals, qualifications, and the best visa pathway for your situation.",
    image: "/assets/process/step-01-occupation.jpg",
  },
  {
    id: "02",
    title: "Eligibility Assessment",
    description:
      "We thoroughly assess your eligibility for various visa options, including skilled migration, family visas, and business visas.",
    image: "/assets/process/step-02-skills-assessment.jpg",
  },
  {
    id: "03",
    title: "Documentation Preparation",
    description:
      "Our team guides you in collecting and preparing all necessary documents, certifications, and evidence required for your application.",
    image: "/assets/process/step-03-english-test.jpg",
  },
  {
    id: "04",
    title: "Application Lodgement",
    description:
      "We ensure your application is complete, accurate, and lodged correctly with the Department of Home Affairs.",
    image: "/assets/process/step-04-calculate-points.jpg",
  },
  {
    id: "05",
    title: "Case Management & Follow-up",
    description:
      "Stay informed throughout the process. We handle all correspondence, requests for additional information, and status updates.",
    image: "/assets/process/step-05-eoi.jpg",
  },
  {
    id: "06",
    title: "Visa Grant & Settlement Support",
    description:
      "Celebrate your visa approval! We provide settlement guidance to help you transition smoothly to life in Australia.",
    image: "/assets/process/step-06-visa-application.jpg",
  },
];

export default function AboutJourneySection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const cardWidth = 280;
  const cardHeight = 380;
  const cardRadius = 20;
  const visibleCards = 2;
  const sideCardScale = 0.9;
  const sideCardOpacity = 0.8;
  const animationDuration = 800;
  const autoPlay = 5000;
  const pauseOnHover = true;
  const grayscaleEffect = true;

  const totalSteps = journeySteps.length;

  const paginate = useCallback(
    (newDirection: number) => {
      if (totalSteps === 0) return;
      setDirection(newDirection);
      const nextIndex = (currentIndex + newDirection + totalSteps) % totalSteps;
      setCurrentIndex(nextIndex);
    },
    [currentIndex, totalSteps]
  );

  const wrapIndex = (index: number) => {
    return (index + totalSteps) % totalSteps;
  };

  const calculatePosition = (index: number) => {
    const activeIndex = currentIndex;
    const diff = wrapIndex(index - activeIndex);

    if (diff === 0) return 'center';
    if (diff <= visibleCards) return `right-${diff}`;
    if (diff >= totalSteps - visibleCards) return `left-${totalSteps - diff}`;
    return 'hidden';
  };

  const getVariantStyles = (position: string): TargetAndTransition => {
    const transition = {
      duration: animationDuration / 1000,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    };

    switch (position) {
      case 'center':
        return {
          zIndex: 10,
          opacity: 1,
          scale: 1.1,
          x: 0,
          filter: 'grayscale(0%)',
          pointerEvents: 'auto',
          transition,
        };
      case 'right-1':
        return {
          zIndex: 5,
          opacity: sideCardOpacity,
          scale: sideCardScale,
          x: cardWidth * 0.7,
          filter: grayscaleEffect ? 'grayscale(100%)' : 'grayscale(0%)',
          pointerEvents: 'auto',
          transition,
        };
      case 'right-2':
        return {
          zIndex: 1,
          opacity: sideCardOpacity * 0.7,
          scale: sideCardScale * 0.9,
          x: cardWidth * 1.4,
          filter: grayscaleEffect ? 'grayscale(100%)' : 'grayscale(0%)',
          pointerEvents: 'auto',
          transition,
        };
      case 'left-1':
        return {
          zIndex: 5,
          opacity: sideCardOpacity,
          scale: sideCardScale,
          x: -cardWidth * 0.7,
          filter: grayscaleEffect ? 'grayscale(100%)' : 'grayscale(0%)',
          pointerEvents: 'auto',
          transition,
        };
      case 'left-2':
        return {
          zIndex: 1,
          opacity: sideCardOpacity * 0.7,
          scale: sideCardScale * 0.9,
          x: -cardWidth * 1.4,
          filter: grayscaleEffect ? 'grayscale(100%)' : 'grayscale(0%)',
          pointerEvents: 'auto',
          transition,
        };
      default:
        return {
          zIndex: 0,
          opacity: 0,
          scale: 0.8,
          x: direction > 0 ? cardWidth * (visibleCards + 1) : -cardWidth * (visibleCards + 1),
          pointerEvents: 'none',
          filter: grayscaleEffect ? 'grayscale(100%)' : 'grayscale(0%)',
          transition,
        };
    }
  };

  // Auto-play functionality
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoPlay > 0) {
      interval = setInterval(() => {
        paginate(1);
      }, autoPlay);
    }

    const carouselContainer = document.getElementById('journey-carousel-container');

    const handleMouseEnter = () => {
      if (pauseOnHover && autoPlay > 0) clearInterval(interval);
    };

    const handleMouseLeave = () => {
      if (pauseOnHover && autoPlay > 0) {
        interval = setInterval(() => {
          paginate(1);
        }, autoPlay);
      }
    };

    if (carouselContainer && pauseOnHover && autoPlay > 0) {
      carouselContainer.addEventListener('mouseenter', handleMouseEnter);
      carouselContainer.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      clearInterval(interval);
      if (carouselContainer && pauseOnHover && autoPlay > 0) {
        carouselContainer.removeEventListener('mouseenter', handleMouseEnter);
        carouselContainer.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [autoPlay, paginate, pauseOnHover]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        paginate(-1);
      } else if (e.key === 'ArrowRight') {
        paginate(1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [paginate]);

  // Touch navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diff = touchStart - touchEnd;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        paginate(1);
      } else {
        paginate(-1);
      }
    }
  };

  return (
    <div
      id="journey-carousel-container"
      className="min-h-screen flex flex-col items-center justify-center overflow-hidden relative bg-white py-20"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Title */}
      <h2
        className="font-black uppercase tracking-tight absolute top-12 left-1/2 transform -translate-x-1/2 pointer-events-none whitespace-nowrap text-6xl md:text-7xl lg:text-8xl text-black"
        style={{}}
      >
        PROCESS
      </h2>

      {/* Carousel Container */}
      <div
        className="w-full max-w-6xl relative mt-20"
        style={{
          height: cardHeight + 100,
          perspective: '1000px',
        }}
      >
        {/* Navigation Arrows */}
        <motion.button
          onClick={() => paginate(-1)}
          className="absolute left-5 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center z-20 transition-all duration-300 hover:scale-110"
          whileTap={{ scale: 0.9 }}
        >
          <ChevronLeft className="w-6 h-6" />
        </motion.button>
        <motion.button
          onClick={() => paginate(1)}
          className="absolute right-5 top-1/2 transform -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white w-10 h-10 rounded-full flex items-center justify-center z-20 transition-all duration-300 hover:scale-110"
          whileTap={{ scale: 0.9 }}
        >
          <ChevronRight className="w-6 h-6" />
        </motion.button>

        {/* Cards Track */}
        <div
          className="w-full h-full flex justify-center items-center relative"
          style={{ transformStyle: 'preserve-3d' }}
        >
          <AnimatePresence initial={false} custom={direction}>
            {journeySteps.map((step, index) => {
              const position = calculatePosition(index);
              const isCurrent = index === currentIndex;

              if (position === 'hidden' && !isCurrent) return null;

              return (
                <motion.div
                  key={step.id}
                  className="absolute bg-white overflow-hidden shadow-2xl cursor-pointer"
                  style={{
                    width: cardWidth,
                    height: cardHeight,
                    borderRadius: cardRadius,
                    top: '50%',
                    left: '50%',
                    marginLeft: -cardWidth / 2,
                    marginTop: -cardHeight / 2,
                  }}
                  initial={getVariantStyles('hidden')}
                  animate={getVariantStyles(position)}
                  exit={getVariantStyles('hidden')}
                  onClick={() => {
                    if (!isCurrent) {
                      const newDirection = index > currentIndex ? 1 : -1;
                      setDirection(newDirection);
                      setCurrentIndex(index);
                    }
                  }}
                >
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      {/* Step Info */}
      {journeySteps[currentIndex] && (
        <motion.div
          key={journeySteps[currentIndex].id + "-info"}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="text-center mt-10 px-4"
        >
          <h2
            className="text-3xl md:text-4xl font-bold mb-3 relative inline-block text-[#12243F]"
          >
            {journeySteps[currentIndex].title}
            <span
              className="absolute top-full left-0 w-full h-0.5 mt-2 bg-sky-600"
            />
          </h2>
          <p className="text-lg md:text-xl font-medium opacity-80 uppercase tracking-wider text-[#12243F] mb-4">
            Step {currentIndex + 1} of {totalSteps}
          </p>
          <p className="text-base mt-4 max-w-lg mx-auto opacity-70 text-[#5B6D88]">
            {journeySteps[currentIndex].description}
          </p>
        </motion.div>
      )}

      {/* Dots Indicator */}
      <div className="flex justify-center gap-3 mt-8">
        {journeySteps.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => {
              if (index !== currentIndex) {
                const newDirection = index > currentIndex ? 1 : -1;
                setDirection(newDirection);
                setCurrentIndex(index);
              }
            }}
            className={cn(
              "w-3 h-3 rounded-full transition-all duration-300",
              index === currentIndex
                ? "scale-125 bg-sky-600"
                : "hover:scale-110 bg-sky-600/40"
            )}
            whileTap={{ scale: 0.9 }}
          />
        ))}
      </div>
    </div>
  );
}
